import {
  CurrencyISOCode,
  FormattedPriceCalculationResponse,
  getFormattedPrice
} from '@atlassiansox/bxp-use-pricing';
import styled from '@emotion/styled';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import PricingExampleIcon from '../../../icons/pricing-example-icon';
import { deviceMediaQueries } from '../../../utils';
import { PricingExampleHeader } from './pricing-example-header';

export type PricingExampleAnnualProps = {
  unitCounts: number[];
  priceCalc: FormattedPriceCalculationResponse;
  currencyISOCode: CurrencyISOCode;
};

export const PricingExampleAnnual = ({
  unitCounts,
  priceCalc,
  currencyISOCode
}: PricingExampleAnnualProps) => {
  const { formattedTotalPrice, formattedBreakdown, breakdown, totalPrice } =
    priceCalc;

  const existingFmtBreakdown = formattedBreakdown[0];
  const totalUnits = breakdown.reduce(
    // linting is broken in pipeline (for no reason) where it thinks these are untyped
    // and freaks out about trying to use plus operand on untyped vars
    (totalUnits: number, { unitsInTier }) => totalUnits + Number(unitsInTier),
    0
  );

  if (!existingFmtBreakdown) {
    throw new Error('Formatted pricing breakdown was undefined.');
  }
  const {
    formattedUnitsAvailableInTier,
    formattedUnitLimitLower,
    formattedUnitLimitUpper
  } = existingFmtBreakdown;

  return (
    <>
      <PricingExampleHeader unitCount={unitCounts[1] || 250} />
      <DetailContainer>
        <PriceBreakDownContainer>
          <div className="price-structure">
            <div className="user-tier">
              <BoldText>
                <FormattedMessage
                  id="userTierLabel"
                  defaultMessage="User tier"
                  description="Label for user tier"
                />
              </BoldText>
              <div>
                <RegularText>
                  {formattedUnitLimitLower} - {formattedUnitLimitUpper}{' '}
                </RegularText>
              </div>
            </div>
            <div>
              <div className="seats">
                <BoldText>
                  <FormattedMessage
                    id="filledSeatsLabel"
                    defaultMessage="Filled seats"
                    description="Label for filled seats"
                  />
                </BoldText>
                <div>
                  <RegularText>{unitCounts[1] || 250}</RegularText>
                </div>
              </div>
              <div className="seats">
                <BoldText>
                  <FormattedMessage
                    id="openSeatsLabel"
                    defaultMessage="Open seats"
                    description="Label for open seats"
                  />
                </BoldText>
                <div>
                  <RegularText>{formattedUnitsAvailableInTier}</RegularText>
                </div>
              </div>
            </div>
          </div>
          <div className="annual-cost">
            <RegularText>
              <FormattedMessage
                id="annualCostLabel"
                defaultMessage="Annual cost: "
                description="Annual cost for users"
              />
            </RegularText>{' '}
            <BoldText>{formattedTotalPrice}</BoldText>
          </div>
          <div className="average-cost">
            <RegularText>
              <FormattedMessage
                id="monthlyCostPerUserMessage"
                defaultMessage="Estimated price per user/per month: "
                description="monthly cost per user"
              />
            </RegularText>{' '}
            <BoldText>
              {getFormattedPrice(currencyISOCode, totalPrice / totalUnits / 12)}
            </BoldText>
          </div>
        </PriceBreakDownContainer>
        <IconContainer>
          <PricingExampleIcon />
        </IconContainer>
      </DetailContainer>
    </>
  );
};

const DetailContainer = styled.div`
  position: relative;
`;

const IconContainer = styled.div`
  position: absolute;
  right: 16px;
  top: 15%;

  svg {
    height: 68px;
    width: 68px;
  }

  ${deviceMediaQueries.desktop} {
    right: 0;
    top: 0;

    svg {
      height: 160px;
      width: 160px;
    }
  }
`;

const PriceBreakDownContainer = styled.div`
  font-family: 'Charlie Text';
  font-size: 14px;
  line-height: 18px;

  .seats {
    padding-top: 8px;
  }

  .annual-cost {
    padding-top: 16px;
  }

  .average-cost {
    padding-bottom: 24px;
    padding-top: 4px;
  }

  .price-structure {
    margin-top: 16px;
  }

  ${deviceMediaQueries.desktop} {
    font-size: 16px;
    line-height: 24px;

    .user-tier {
      padding-top: 8px;
    }

    .price-structure {
      display: flex;
      justify-content: space-between;
      width: 271px;
    }

    .annual-cost {
      padding-top: 48px;
    }
  }
`;

const RegularText = styled.span`
  font-weight: 400;
`;

const BoldText = styled.span`
  font-weight: 700;
`;
